import {action, observable, runInAction} from 'mobx';

import {
    useAddApiClientDevHubMutation,
    useApiClientsDevHubQuery,
    useDeleteApiClientDevHubMutation,
} from 'src/generated/graphql';

import {CreateApiClientForm, CreateModalStore} from './create-modal-store';
import {DeleteApiClientForm} from './delete-modal-store';
import {RootStore} from './root-store';

export type ApiClient = {
    clientId: string;
    clientName: string;
    scopes: string[];
};

export type CreatedApiClient = ApiClient & {secret: string};

export type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

export class ApiClientStore {
    @observable apiClients: Array<ApiClient> = [];
    @observable loadingState: LoadingState = 'idle';
    @observable errorState?: Error;

    public createModalStore: CreateModalStore;

    constructor(public rootStore: RootStore) {
        this.createModalStore = new CreateModalStore(this);
    }

    @action
    async addApiClient(form: CreateApiClientForm): Promise<CreatedApiClient> {
        // make API call here and get generated client id from api
        const fetcher = useAddApiClientDevHubMutation.fetcher({
            name: form.clientName,
            scopes: form.scopes,
        });

        try {
            const apiClientCreateResponse = await fetcher();

            const response = apiClientCreateResponse?.apiClientCreate;
            const createdClient = response?.apiClient;

            if (!response || !createdClient || !response?.clientSecret) {
                throw new Error('Client could not be created');
            }

            const apiClient = {
                clientId: createdClient.id,
                clientName: createdClient.name,
                scopes: createdClient.scopes,
            };

            runInAction(() => {
                this.apiClients.push(apiClient);
            });

            return {...apiClient, secret: response.clientSecret};
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            throw new Error('Client could not be created');
        }
    }

    @action
    async deleteApiClient(form: DeleteApiClientForm) {
        try {
            const response = await useDeleteApiClientDevHubMutation.fetcher({
                clientId: form.clientId,
            })();
            const clientId = response?.apiClientDelete?.clientId;
            if (!clientId) {
                throw new Error('Client could not be deleted');
            }

            runInAction(() => {
                this.apiClients = this.apiClients.filter((c) => c.clientId !== clientId);
            });
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            throw new Error('Client could not be deleted');
        }
    }

    @action
    async fetchApiClients() {
        this.loadingState = 'loading';
        try {
            const apiClientsResponse = await useApiClientsDevHubQuery.fetcher()();
            runInAction(() => {
                this.loadingState = 'loaded';
                this.apiClients =
                    apiClientsResponse?.apiClients.map((apiClient) => {
                        return {
                            clientId: apiClient.id,
                            clientName: apiClient.name,
                            scopes: apiClient.scopes,
                        };
                    }) || [];
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error fetching api clients', e);
            runInAction(() => {
                this.loadingState = 'error';
            });
        }
    }
}
